import {
    CSSProperties,
    ChangeEvent,
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useMemo,
    type ReactNode,
    useRef,
    useState,
} from 'react'
import { Crepe } from '@milkdown/crepe'
import { Milkdown, useEditor, useInstance } from '@milkdown/react'
import { replaceAll, insert } from '@milkdown/kit/utils'
import {
    commandsCtx,
    defaultValueCtx,
    Editor,
    EditorStatus,
    editorViewCtx,
    editorViewOptionsCtx,
    inputRulesCtx,
    remarkStringifyOptionsCtx,
    rootCtx,
    serializerCtx,
} from '@milkdown/kit/core'
import { commonmark } from '@milkdown/kit/preset/commonmark'
import { history } from '@milkdown/kit/plugin/history'
import { listener, listenerCtx } from '@milkdown/kit/plugin/listener'

import { isTextTagCategory } from '@campai/lib-common/utils/misc'

import '@milkdown/crepe/theme/common/style.css'
import '@milkdown/crepe/theme/frame.css'

import { cn } from '@/components/new/lib/utils'
import { useIsMobile } from '@/components/new/hooks'
import { InputGroup, InputGroupAddon } from '@/components/new/ui/input-group'

import { RichTextEditorCopy } from './copy'
import { PortalHost } from './PortalHost'
import { CharacterLimitPlugin, NoLineBreakPlugin } from './editor/plugins'
import { executeRichTextEditorAction, RichTextEditorActionId } from './editor/actions'
import {
    configureRichTextEditorMenu,
    richTextEditorActionsCtx,
    richTextEditorMenu,
    richTextEditorMenuApiCtx,
} from './editor/slash-menu'
import { allowedTextTagsCtx, textTagPlugin } from './editor/text-tags'
import { insertImageMarkdown, replaceResourceIdsWithUrls, replaceResourceUrlsWithIds } from './resource-markdown'

import type { CodeMirrorFeatureConfig } from '@milkdown/crepe/feature/code-mirror'
import type { Ctx } from '@milkdown/kit/ctx'
import type { Text } from 'mdast'
import type { RichTextEditorError, RichTextEditorHandle, RichTextEditorProps, RichTextEditorUploadResult } from './types'

const normalizeNoLineBreaks = (value: string) => value.replace(/[\r\n]+/g, ' ').trimEnd()
const checkedTaskIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect width="16" height="16" rx="3" fill="#16a34a"/>
    <path
        d="M4 8.2 6.7 11 12 5"
        fill="none"
        stroke="#ffffff"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
    />
</svg>
`
const uncheckedTaskIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect
        x="1"
        y="1"
        width="14"
        height="14"
        rx="3"
        fill="var(--task-checkbox-background)"
        stroke="var(--task-checkbox-border)"
    />
</svg>
`
const extractPlaceholderTokens = (value: string) =>
    Array.from(value.matchAll(/\[\[([^[\]]+)\]\]/g))
        .map((match) => match[1]?.trim())
        .filter(Boolean) as string[]

export type RichTextEditorCoreHandle = RichTextEditorHandle & {
    openImagePicker: () => void
    runAction: (actionId: RichTextEditorActionId) => void
}

type RichTextEditorCoreProps = Omit<RichTextEditorProps, 'errors' | 'onErrorsChange' | 'renderToolbarEnd'> & {
    copy: RichTextEditorCopy
    inlineEndContent?: ReactNode
    invalid?: boolean
    onValidationErrorsChange: (errors: RichTextEditorError[]) => void
    onSelectionStateChange?: (hasSelection: boolean) => void
    useInlineInputShell?: boolean
}

export const RichTextEditorCore = forwardRef<RichTextEditorCoreHandle, RichTextEditorCoreProps>(
    (
        {
            autoFocus,
            compact = false,
            features: { codeBlock = false, headings = true, quote = false, taskList = false } = {},
            imageUpload,
            inlineWrap = false,
            inlineEndContent,
            invalid,
            maxLength,
            maxRows = 16,
            minLength,
            minRows,
            mode = 'rich',
            onChange,
            onValidationErrorsChange,
            onSelectionStateChange,
            placeholder,
            tags = [],
            readOnly,
            resources,
            syntaxHighlighting,
            useInlineInputShell = false,
            validate,
            value,
            copy,
        },
        ref
    ) => {
        const isMobile = useIsMobile()
        const portalTarget = useId().replace(/:/g, '-')
        const fileInputRef = useRef<HTMLInputElement>(null)
        const stableOnChange = useRef(onChange)
        const [resourceState, setResourceState] = useState<Array<{ id: string; url: string }>>(resources ?? [])
        const [internalValue, setInternalValue] = useState(() => replaceResourceIdsWithUrls(value, resources ?? []))
        const internalValueRef = useRef(internalValue)
        const lastCommittedValueRef = useRef(value)
        const isRichMode = mode === 'rich' || mode === 'rich-inline'
        const isInlineMode = mode === 'inline' || mode === 'rich-inline'
        const stackInlineActions = isMobile && !!inlineEndContent
        const [codeMirrorFeatureConfig, setCodeMirrorFeatureConfig] = useState<CodeMirrorFeatureConfig | null>(null)
        // Crash on prod if we don't wait for async CodeMirror setup before mounting Milkdown, otherwise list-heavy content can initialize before editorViewCtx is available
        const [isCodeMirrorReady, setIsCodeMirrorReady] = useState(!syntaxHighlighting)

        useEffect(() => {
            stableOnChange.current = onChange
        }, [onChange])

        useEffect(() => {
            setResourceState((currentResources) => {
                const nextResources = [...currentResources]

                ;(resources ?? []).forEach((resource) => {
                    if (
                        nextResources.some(
                            (candidate) => candidate.id === resource.id && candidate.url === resource.url
                        )
                    ) {
                        return
                    }

                    nextResources.push(resource)
                })

                return nextResources
            })
        }, [resources])

        const displayValue = useMemo(() => replaceResourceIdsWithUrls(value, resourceState), [resourceState, value])
        const allowedTags = useMemo(
            () => tags.flatMap((tag) => (isTextTagCategory(tag) ? [] : [{ ...tag, tag: tag.tag.toLowerCase() }])),
            [tags]
        )
        const allowedTagTexts = useMemo(() => allowedTags.map((tag) => tag.tag), [allowedTags])

        const serializedValue = useMemo(() => {
            const nextValue = replaceResourceUrlsWithIds(internalValue, resourceState)
            return isInlineMode ? normalizeNoLineBreaks(nextValue) : nextValue
        }, [internalValue, isInlineMode, resourceState])

        useEffect(() => {
            if (!syntaxHighlighting) {
                setCodeMirrorFeatureConfig(null)
                setIsCodeMirrorReady(true)
                return
            }

            let stale = false

            setIsCodeMirrorReady(false)

            Promise.all([import('codemirror'), import('@/components/syntaxHighlighting/codeMirror')])
                .then(([{ minimalSetup }, { codeMirrorSyntaxHighlightingExtension }]) => {
                    if (stale) {
                        return
                    }

                    setCodeMirrorFeatureConfig({
                        extensions: [minimalSetup, codeMirrorSyntaxHighlightingExtension],
                        renderPreview: () => null,
                    })
                    setIsCodeMirrorReady(true)
                })
                .catch(() => {
                    if (!stale) {
                        setCodeMirrorFeatureConfig(null)
                        setIsCodeMirrorReady(true)
                    }
                })

            return () => {
                stale = true
            }
        }, [syntaxHighlighting])

        const handleUploadFile = useCallback(
            async (file: File): Promise<RichTextEditorUploadResult | null> => {
                if (!imageUpload) {
                    return null
                }

                const uploadResult = await imageUpload(file)
                if (!uploadResult) {
                    return null
                }

                setResourceState((currentResources) => {
                    if (currentResources.some((resource) => resource.id === uploadResult.id)) {
                        return currentResources
                    }

                    return [...currentResources, { id: uploadResult.id, url: uploadResult.url }]
                })

                return uploadResult
            },
            [imageUpload]
        )

        useEditor(
            (root) => {
                const simplePreset = commonmark.filter((plugin) => {
                    const group = plugin.meta?.group ?? ''
                    return !group || ['Doc', 'Text', 'Paragraph'].includes(group)
                })

                const buildRichTextEditor = () => {
                    const codeMirrorEnabled = !!codeMirrorFeatureConfig
                    const editor = new Crepe({
                        root,
                        defaultValue: displayValue,
                        features: {
                            [Crepe.Feature.BlockEdit]: false,
                            [Crepe.Feature.CodeMirror]: codeMirrorEnabled,
                            [Crepe.Feature.Latex]: false,
                            [Crepe.Feature.Toolbar]: false,
                        },
                        featureConfigs: {
                            ...(codeMirrorFeatureConfig
                                ? {
                                      [Crepe.Feature.CodeMirror]: codeMirrorFeatureConfig,
                                  }
                                : {}),
                            [Crepe.Feature.Cursor]: {
                                virtual: false,
                            },
                            [Crepe.Feature.ListItem]: {
                                checkBoxCheckedIcon: checkedTaskIcon,
                                checkBoxUncheckedIcon: uncheckedTaskIcon,
                            },
                            [Crepe.Feature.Placeholder]: {
                                mode: 'block',
                                text: placeholder ?? (isInlineMode ? '' : copy.editorPlaceholder),
                            },
                            [Crepe.Feature.ImageBlock]: {
                                onUpload: async (file) => {
                                    const result = await handleUploadFile(file)
                                    return result?.url ?? ''
                                },
                            },
                        },
                    })

                    editor.editor.use(richTextEditorMenu).use(richTextEditorActionsCtx).use(richTextEditorMenuApiCtx)
                    editor.editor.config((ctx) => {
                        configureRichTextEditorMenu(ctx, {
                            codeBlock,
                            portalTarget,
                            headings,
                            copy,
                            imageUploadEnabled: !!imageUpload,
                            mode,
                            quote,
                            taskList,
                        })

                        ctx.set(richTextEditorActionsCtx.key, {
                            openImagePicker: () => fileInputRef.current?.click(),
                        })
                    })

                    editor.on((listeners) => {
                        listeners.selectionUpdated((_ctx, selection) => {
                            onSelectionStateChange?.(!selection.empty)
                        })

                        listeners.updated((ctx, doc) => {
                            let markdown: string

                            try {
                                markdown = ctx.get(serializerCtx)(doc)
                            } catch (error) {
                                console.warn('Suppressed RichTextEditor markdown serialization error:', error)
                                return
                            }

                            const nextValue = isInlineMode ? normalizeNoLineBreaks(markdown) : markdown

                            if (internalValueRef.current === nextValue) {
                                return
                            }

                            internalValueRef.current = nextValue
                            setInternalValue(nextValue)
                        })
                    })

                    window.setTimeout(() => {
                        editor.setReadonly(readOnly ?? false)
                    }, 10)

                    return editor
                }

                const buildSimpleEditor = () => {
                    return Editor.make()
                        .config((ctx) => {
                            ctx.set(rootCtx, root)
                            ctx.set(defaultValueCtx, displayValue)
                            ctx.update(inputRulesCtx, () => [])
                            ctx.get(listenerCtx).updated((listenerCtx, doc) => {
                                let markdown: string

                                try {
                                    markdown = listenerCtx.get(serializerCtx)(doc)
                                } catch (error) {
                                    console.warn('Suppressed RichTextEditor markdown serialization error:', error)
                                    return
                                }

                                const nextValue = isInlineMode ? normalizeNoLineBreaks(markdown) : markdown

                                if (internalValueRef.current === nextValue) {
                                    return
                                }

                                internalValueRef.current = nextValue
                                setInternalValue(nextValue)
                            })

                            if (readOnly) {
                                ctx.update(editorViewOptionsCtx, (currentOptions) => ({
                                    ...(currentOptions ?? {}),
                                    editable: () => false,
                                }))
                            }
                        })
                        .use(simplePreset)
                        .use(listener)
                        .use(history)
                }

                const component = isRichMode ? buildRichTextEditor() : buildSimpleEditor()
                const editor = component instanceof Crepe ? component.editor : component

                editor.use(textTagPlugin()).use((ctx) => {
                    ctx.set(allowedTextTagsCtx.key, {
                        allowedTags,
                        allowedTagsText: allowedTagTexts,
                    })

                    return () => {}
                })

                if (isInlineMode) {
                    editor.use(NoLineBreakPlugin)
                }

                if (maxLength) {
                    editor.use(CharacterLimitPlugin(maxLength))
                }

                editor.config((ctx: Ctx) =>
                    ctx.set(remarkStringifyOptionsCtx, {
                        unsafe: [],
                        handlers: {
                            text(node: Text) {
                                return node.value
                            },
                        },
                    })
                )

                return component
            },
            [codeMirrorFeatureConfig]
        )
        const [isEditorLoading, getEditorInstance] = useInstance()
        const getEditor = useCallback(
            () => (isEditorLoading ? undefined : getEditorInstance()),
            [getEditorInstance, isEditorLoading]
        )

        useEffect(() => {
            // Inline editors normalize stored line breaks on mount; that is not a user edit.
            lastCommittedValueRef.current = isInlineMode ? normalizeNoLineBreaks(value) : value
            const nextDisplayValue = replaceResourceIdsWithUrls(value, resourceState)

            if (nextDisplayValue === internalValueRef.current) {
                return
            }

            setInternalValue(nextDisplayValue)
        }, [isInlineMode, resourceState, value])

        useEffect(() => {
            if (internalValueRef.current !== internalValue) {
                internalValueRef.current = internalValue
                requestAnimationFrame(() => {
                    getEditor()?.action(replaceAll(internalValue, true))
                })
            }
        }, [getEditor, internalValue])

        useEffect(() => {
            if (serializedValue === lastCommittedValueRef.current) {
                return
            }

            lastCommittedValueRef.current = serializedValue
            stableOnChange.current(serializedValue)
        }, [serializedValue])

        useEffect(() => {
            const editor = getEditor()
            if (!editor || !readOnly) {
                return
            }

            editor.action((ctx) => {
                const view = ctx.get(editorViewCtx)
                if (editor.status === EditorStatus.Created) {
                    view.setProps({
                        editable: () => false,
                    })
                }
            })
        }, [getEditor, readOnly])

        useEffect(() => {
            if (!autoFocus) {
                return
            }

            window.setTimeout(() => {
                getEditor()?.action((ctx) => {
                    ctx.get(editorViewCtx).focus()
                })
            }, 0)
        }, [autoFocus, getEditor])

        useEffect(() => {
            if (!isRichMode || readOnly) {
                return
            }

            const editor = getEditor()

            if (!editor || editor.status !== EditorStatus.Created) {
                return
            }

            let cleanup: (() => void) | undefined

            editor.action((ctx) => {
                const view = ctx.get(editorViewCtx)

                if (!(view.dom instanceof HTMLElement)) {
                    return
                }

                const onKeyDown = (event: KeyboardEvent) => {
                    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) {
                        return
                    }

                    window.requestAnimationFrame(() => {
                        editor.action((nextCtx) => {
                            const nextView = nextCtx.get(editorViewCtx)
                            const pos = nextView.state.selection.from
                            nextCtx.get(richTextEditorMenuApiCtx.key).show(pos)
                        })
                    })
                }

                view.dom.addEventListener('keydown', onKeyDown)
                cleanup = () => view.dom.removeEventListener('keydown', onKeyDown)
            })

            return () => {
                cleanup?.()
            }
        }, [getEditor, isRichMode, readOnly])

        useEffect(() => {
            const editor = getEditor()

            if (!editor || editor.status !== EditorStatus.Created || tags.length === 0) {
                return
            }

            let refreshTimeout: number | undefined

            editor.action((ctx) => {
                ctx.set(allowedTextTagsCtx.key, {
                    allowedTags,
                    allowedTagsText: allowedTagTexts,
                })

                refreshTimeout = window.setTimeout(() => {
                    if (!editor || editor.status !== EditorStatus.Created) {
                        return
                    }

                    try {
                        editor.action((nextCtx) => {
                            nextCtx.get(commandsCtx).call('RefreshTagNodes')
                        })
                    } catch (error) {
                        console.warn('Suppressed RefreshTagNodes error:', error)
                    }
                }, 1)

                return () => {}
            })

            return () => {
                if (refreshTimeout !== undefined) {
                    window.clearTimeout(refreshTimeout)
                }
            }
        }, [allowedTagTexts, allowedTags, getEditor, tags.length])

        useEffect(() => {
            const timeout = window.setTimeout(async () => {
                const nextErrors: RichTextEditorError[] = []
                const usedTags = extractPlaceholderTokens(serializedValue)
                const allowedTagTextsSet = new Set(allowedTagTexts)
                const invalidTags = usedTags.filter((tag) => !allowedTagTextsSet.has(tag.toLowerCase()))

                if (invalidTags.length > 0) {
                    nextErrors.push({
                        type: 'tags',
                        message: copy.errors.tagsNotAllowed(invalidTags.join(', ')),
                    })
                }

                if (minLength && serializedValue.length < minLength) {
                    nextErrors.push({
                        type: 'minLength',
                        message: copy.errors.minLength(minLength),
                    })
                }

                if (maxLength && serializedValue.length > maxLength) {
                    nextErrors.push({
                        type: 'maxLength',
                        message: copy.errors.maxLength(maxLength),
                    })
                }

                if (validate) {
                    const customErrors = await validate(serializedValue)

                    customErrors.forEach((message) => {
                        nextErrors.push({
                            type: 'custom',
                            message,
                        })
                    })
                }

                onValidationErrorsChange(nextErrors)
            }, 200)

            return () => window.clearTimeout(timeout)
        }, [allowedTagTexts, copy.errors, maxLength, minLength, onValidationErrorsChange, serializedValue, validate])

        const focus = useCallback(() => {
            getEditor()?.action((ctx) => {
                ctx.get(editorViewCtx).focus()
            })
        }, [getEditor])

        const insertTextValue = useCallback(
            (text: string) => {
                const editor = getEditor()
                editor?.action(insert(text, true))

                window.setTimeout(() => {
                    editor?.action((ctx) => {
                        ctx.get(editorViewCtx).focus()
                    })
                }, 10)
            },
            [getEditor]
        )

        const insertTag = useCallback(
            (tag: string) => {
                const editor = getEditor()

                if (tags.length > 0) {
                    editor?.action((ctx) => {
                        ctx.get(commandsCtx).call('InsertTag', tag)
                    })
                } else {
                    editor?.action(insert(`[[${tag}]]`, true))
                }

                window.setTimeout(() => {
                    editor?.action((ctx) => {
                        ctx.get(editorViewCtx).focus()
                    })
                }, 10)
            },
            [getEditor, tags.length]
        )

        const openImagePicker = useCallback(() => {
            fileInputRef.current?.click()
        }, [])

        const runAction = useCallback(
            (actionId: RichTextEditorActionId) => {
                const editor = getEditor()

                editor?.action((ctx) => {
                    executeRichTextEditorAction(actionId, ctx, {
                        openImagePicker,
                    })
                })

                window.setTimeout(() => {
                    editor?.action((ctx) => {
                        ctx.get(editorViewCtx).focus()
                    })
                }, 10)
            },
            [getEditor, openImagePicker]
        )

        useImperativeHandle(
            ref,
            () => ({
                focus,
                insertText: insertTextValue,
                insertTag,
                openImagePicker,
                runAction,
            }),
            [focus, insertTag, insertTextValue, openImagePicker, runAction]
        )

        const handleImageInputChange = useCallback(
            async (event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0]
                event.target.value = ''

                if (!file) {
                    return
                }

                const uploadResult = await handleUploadFile(file)
                if (!uploadResult) {
                    return
                }

                insertTextValue(insertImageMarkdown(uploadResult.url, uploadResult.alt))
            },
            [handleUploadFile, insertTextValue]
        )

        const wrapperStyle = useMemo(() => {
            const rowHeight = 20
            const inlineWrapBorderHeight = isInlineMode ? 2 : 0

            const verticalPadding = isInlineMode && inlineWrap ? 16 - inlineWrapBorderHeight : 20

            return {
                minHeight: `${Math.max(
                    isInlineMode ? 36 - inlineWrapBorderHeight : 40,
                    (minRows ?? 0) * rowHeight + verticalPadding
                )}px`,
                maxHeight: maxRows ? `${maxRows * rowHeight + verticalPadding}px` : undefined,
            } satisfies CSSProperties
        }, [inlineWrap, isInlineMode, maxRows, minRows])

        const handleEditorContainerMouseDown = useCallback(
            (event: React.MouseEvent<HTMLDivElement>) => {
                if (readOnly) {
                    return
                }

                const target = event.target as HTMLElement

                if (
                    target.closest(
                        'button, input, textarea, select, a, [role="button"], [data-slot="combobox-trigger-button"], [data-slot="popover-content"]'
                    )
                ) {
                    return
                }

                if (target.closest('.ProseMirror')) {
                    return
                }

                window.setTimeout(() => {
                    focus()
                }, 0)
            },
            [focus, readOnly]
        )

        const editorContent = (
            <div
                data-mode={mode}
                className={cn(
                    'flex min-h-0 flex-1',
                    useInlineInputShell && 'rich-text-editor-inline-input min-w-0',
                    useInlineInputShell && !inlineWrap && 'h-full',
                    useInlineInputShell && inlineWrap && 'rich-text-editor-inline-input-wrap'
                )}
                onMouseDown={handleEditorContainerMouseDown}
                style={
                    {
                        '--rich-text-editor-min-height': wrapperStyle.minHeight,
                        maxHeight: wrapperStyle.maxHeight,
                    } as CSSProperties
                }
            >
                <div
                    className={cn(
                        'flex min-h-0 flex-1 overflow-auto',
                        syntaxHighlighting && isRichMode && 'rich-text-editor-code-scroll',
                        isInlineMode && 'rich-text-editor-inline-scrollbar-hidden w-full min-w-0'
                    )}
                >
                    <div className="flex min-h-0 flex-1">{isCodeMirrorReady && <Milkdown />}</div>
                </div>
            </div>
        )

        return (
            <>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageInputChange}
                    tabIndex={-1}
                />
                {isRichMode ? <PortalHost id={portalTarget} /> : null}
                {useInlineInputShell ? (
                    <InputGroup
                        data-slot="rich-text-editor-inline-input"
                        data-invalid={invalid || undefined}
                        className={cn(
                            'rich-text-editor bg-background dark:bg-input/30 focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-3 min-h-9 items-stretch px-0',
                            inlineWrap && 'h-auto',
                            !inlineWrap && 'h-9',
                            stackInlineActions && 'h-auto flex-col',
                            invalid && 'border-destructive ring-destructive/20 dark:ring-destructive/40 ring-3'
                        )}
                    >
                        {editorContent}
                        {inlineEndContent ? (
                            <InputGroupAddon
                                align={stackInlineActions ? 'block-end' : 'inline-end'}
                                className={cn(
                                    'gap-1',
                                    compact ? 'pr-2' : 'pr-1',
                                    inlineWrap && 'py-0',
                                    stackInlineActions && 'border-border flex-wrap border-t px-1 py-1'
                                )}
                            >
                                {inlineEndContent}
                            </InputGroupAddon>
                        ) : null}
                    </InputGroup>
                ) : (
                    editorContent
                )}
            </>
        )
    }
)

RichTextEditorCore.displayName = 'RichTextEditorCore'
